import axios from 'axios';

function isLocalWebHost(hostname) {
  const host = String(hostname || '').trim().toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.local');
}

function resolveApiBase() {
  const configured = String(process.env.REACT_APP_API_URL || '').trim();
  const clean = (value) => String(value || '').trim().replace(/\/+$/, '');
  if (typeof window === 'undefined') {
    return clean(configured || 'http://localhost:5000/api');
  }
  const host = String(window.location.hostname || '').trim().toLowerCase();
  if (isLocalWebHost(host)) {
    return clean(configured || 'http://localhost:5000/api');
  }
  if (configured && !/localhost|127\.0\.0\.1/i.test(configured)) {
    return clean(configured);
  }
  if (host === 'pluginserver.gotchat.ai') return clean(`${window.location.origin}/api`);
  if (host === 'account.gotchat.ai') return 'https://pluginserver.gotchat.ai/api';
  return 'https://pluginserver.gotchat.ai/api';
}

export const API_BASE = resolveApiBase();

const publicJsonCache = new Map();

function getPublicCacheStorage() {
try {
return typeof window !== 'undefined' ? window.sessionStorage : null;
} catch {
return null;
}
}

function publicCacheKey(url) {
return `pluginrepo:public-json:${url}`;
}

export async function publicCachedGet(url, ttlMs = 60000) {
const now = Date.now();
const key = publicCacheKey(url);
const memoryEntry = publicJsonCache.get(key);
if (memoryEntry && memoryEntry.expiresAt > now) {
return { data: memoryEntry.data, cached: true };
}

const storage = getPublicCacheStorage();
if (storage) {
try {
const raw = storage.getItem(key);
if (raw) {
const parsed = JSON.parse(raw);
if (parsed.expiresAt > now) {
publicJsonCache.set(key, parsed);
return { data: parsed.data, cached: true };
}
storage.removeItem(key);
}
} catch {
storage.removeItem(key);
}
}

const response = await axios.get(url);
const entry = {
data: response.data,
expiresAt: now + ttlMs
};
publicJsonCache.set(key, entry);
if (storage) {
try {
storage.setItem(key, JSON.stringify(entry));
} catch {
// Storage can be full or disabled; the in-memory cache still helps.
}
}
return response;
}

// Create axios instance with default config
const api = axios.create({
baseURL: API_BASE,
headers: {
'Content-Type': 'application/json'
}
});

// Add request interceptor to include token
api.interceptors.request.use(
(config) => {
const token = localStorage.getItem('token');
if (token) {
config.headers.Authorization = `Bearer ${token}`;
}
return config;
},
(error) => {
return Promise.reject(error);
}
);

// Add response interceptor to handle errors
api.interceptors.response.use(
(response) => response,
(error) => {
if (error.response?.status === 401) {
// Token expired or invalid
localStorage.clear();
window.location.href = '/login';
}
return Promise.reject(error);
}
);

// API methods
export const authAPI = {
register: (data) => api.post('/account/register', data),
login: (data) => api.post('/account/login', data),
getProfile: () => api.get('/account/profile'),
updateProfile: (data) => api.put('/account/profile', data),
changePassword: (data) => api.post('/account/change-password', data)
};

export const pluginAPI = {
getApproved: () => api.get('/plugin/approved'),
getById: (id) => api.get(`/plugin/${id}`),
search: (query) => api.get(`/plugin/search?q=${query}`),
getDownloadUrl: (id) => api.get(`/plugin/${id}/download`),
getUserPlugins: (userId) => api.get(`/plugin/user/${userId}`),
getMine: () => api.get('/plugin/mine'),
upload: (formData) => api.post('/plugin/upload', formData, {
headers: { 'Content-Type': 'multipart/form-data' }
}),
delete: (id) => api.delete(`/plugin/${id}`),
getPending: () => api.get('/plugin/pending'),
approve: (id) => api.post(`/plugin/${id}/approve`),
reject: (id, reason) => api.post(`/plugin/${id}/reject`, { reason })
};

export const reviewAPI = {
getPluginReviews: (pluginId) => api.get(`/review/plugin/${pluginId}`),
create: (data) => api.post('/review', data),
reply: (reviewId, reply) => api.post(`/review/${reviewId}/reply`, { reply })
};

export const bugAPI = {
getPluginBugs: (pluginId, status) =>
api.get(`/bugreport/plugin/${pluginId}${status ? `?status=${status}` : ''}`),
getById: (id) => api.get(`/bugreport/${id}`),
create: (data) => api.post('/bugreport', data),
addComment: (id, comment) => api.post(`/bugreport/${id}/comment`, { comment }),
updateStatus: (id, status) => api.put(`/bugreport/${id}/status`, { status })
};

export const adminAPI = {
getAllowedExtensions: () => api.get('/admin/allowed-extensions'),
updateAllowedExtensions: (extensions) => api.put('/admin/allowed-extensions', extensions),
getMalwareScanning: () => api.get('/admin/malware-scanning'),
updateMalwareScanning: (enabled) => api.put('/admin/malware-scanning', { enabled }),
getPaymentSettings: () => api.get('/admin/payment-settings'),
updatePaymentSettings: (settings) => api.put('/admin/payment-settings', settings)
};

export const marketplaceAPI = {
getProviders: () => api.get('/marketplace/providers'),
getSellerAccounts: () => api.get('/marketplace/seller/accounts'),
saveSellerAccount: (provider, data) => api.put(`/marketplace/seller/${provider}`, data),
getEstimate: (pluginId, payCurrency) => api.get(`/marketplace/plugins/${pluginId}/estimate?payCurrency=${encodeURIComponent(payCurrency)}`),
getAccess: (pluginId) => api.get(`/marketplace/plugins/${pluginId}/access`),
createCheckout: (pluginId, data) => api.post(`/marketplace/plugins/${pluginId}/checkout`, data),
captureCheckout: (providerOrderId) => api.post(`/marketplace/checkout/${encodeURIComponent(providerOrderId)}/capture`, {}),
getCheckoutStatus: (providerOrderId) => api.get(`/marketplace/checkout/${encodeURIComponent(providerOrderId)}/status`)
};

export const appReleaseAPI = {
getAdminList: () => api.get('/app-release/admin/list'),
getBySlug: (slug) => api.get(`/app-release/${slug}`),
upsert: (data) => api.post('/app-release/admin/upsert', data),
delete: (slug) => api.delete(`/app-release/admin/${encodeURIComponent(slug)}`),
upload: (formData) => api.post('/app-release/admin/upload', formData, {
headers: { 'Content-Type': 'multipart/form-data' }
})
};

export default api;
